/**
 * Export Utilities
 * 
 * Functions for exporting data to PDF and CSV formats
 */

// Import jsPDF - for v4.x use named import  
import { jsPDF } from 'jspdf';
// Import autoTable plugin - this extends jsPDF prototype when imported
// Must be imported after jsPDF
import 'jspdf-autotable';
// Import company logo
import companyLogo from '../assets/img/image.png';
// Import auth service to get current user
import { getCurrentUser } from '../services/authService';

/**
 * Export data to CSV
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions [{ key, label }]
 * @param {string} filename - Name of the file to download
 */
export const exportToCSV = (data, columns, filename = 'export.csv') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Create CSV header
  const headers = columns.map(col => col.label || col.key);
  const csvRows = [headers.join(',')];

  // Create CSV rows
  data.forEach(item => {
    const values = columns.map(col => {
      const value = item[col.key];
      // Handle null/undefined values
      if (value === null || value === undefined) {
        return '';
      }
      // Escape commas and quotes in values
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    });
    csvRows.push(values.join(','));
  });

  // Create CSV content
  const csvContent = csvRows.join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Load image and convert to base64
 * @param {string} imagePath - Path to the image
 * @returns {Promise<string>} - Base64 data URL
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      try {
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = reject;
    img.src = imagePath;
  });
};

/**
 * Export data to PDF
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Array of column definitions [{ key, label }]
 * @param {string} title - Title of the document
 * @param {string} filename - Name of the file to download
 */
export const exportToPDF = async (data, columns, title = 'Export', filename = 'export.pdf') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Create new PDF document - A4 size in portrait orientation
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  // Company colors
  const companyBlue = [13, 71, 161]; // Dark blue for table header
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Load company logo once and cache it
  let logoDataUrl = null;
  try {
    logoDataUrl = await loadImageAsBase64(companyLogo);
  } catch (error) {
    console.warn('Could not load company logo:', error);
  }
  
  // Get current user for "Generated by" field
  const currentUser = getCurrentUser();
  const generatedBy = currentUser?.name || currentUser?.email || 'System Administrator';
  
  // Get current year for document title
  const currentYear = new Date().getFullYear();
  
  // Function to draw header (reusable for all pages)
  const drawHeader = () => {
    // Add company logo at top left - positioned higher
    if (logoDataUrl) {
      const logoWidth = 20; // mm - slightly smaller
      const logoHeight = 20; // mm
      const logoX = 14; // Left margin
      const logoY = 10; // Top margin - start higher
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
    }
    
    // Add company name next to logo - with proper vertical alignment and spacing
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0); // Black text
    doc.setFont(undefined, 'bold');
    const companyName = 'AFRIQ NORTHWEST';
    const logoEndX = logoDataUrl ? 14 + 20 + 8 : 14; // Logo width + spacing
    const textY = logoDataUrl ? 10 + 10 : 10; // Center vertically with logo (logoY + logoHeight/2)
    doc.text(companyName, logoEndX, textY);
  };
  
  // Function to draw footer (reusable for all pages)
  const drawFooter = () => {
    const footerY = pageHeight - 15;
    
    // Copyright
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text('Copyright AFRIQ NW', pageWidth / 2, footerY, { align: 'center' });
    
    // Powered by
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Powered by deepxlabs', pageWidth / 2, footerY + 5, { align: 'center' });
  };
  
  // Draw header on first page
  drawHeader();
  
  // Add document details section with proper spacing
  // Logo starts at Y=10, height=20, so ends at Y=30
  // 30px spacing = 30 * 0.264583 = ~8mm (converting px to mm at 96 DPI)
  const logoBottomY = logoDataUrl ? 10 + 20 : 10;
  const spacing30px = 8; // 30px in mm
  const detailsStartY = logoBottomY + spacing30px; // 30px spacing below logo
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  const documentTitle = `${title} ${currentYear}`;
  doc.text(documentTitle, pageWidth / 2, detailsStartY, { align: 'center' });
  
  // Add generated date and by - with proper line spacing
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'normal');
  const generatedOnY = detailsStartY + 6;
  const generatedByY = detailsStartY + 12;
  doc.text(`Generated on: ${date}`, pageWidth / 2, generatedOnY, { align: 'center' });
  doc.text(`Generated by: ${generatedBy}`, pageWidth / 2, generatedByY, { align: 'center' });
  
  // Calculate header height (logo + details section + 20px spacing before table)
  // 20px spacing = 20 * 0.264583 = ~5.3mm
  const spacing20px = 5.3; // 20px in mm
  const headerHeight = generatedByY + 5 + spacing20px; // Last text Y + line height + 20px spacing before table
  
  // Prepare table data - allow text to wrap naturally
  const tableData = data.map(item => 
    columns.map(col => {
      const value = item[col.key];
      if (value === null || value === undefined) {
        return '';
      }
      // Convert to string - let autoTable handle wrapping
      const stringValue = String(value);
      // Only truncate extremely long text (over 500 chars) to prevent memory issues
      return stringValue.length > 500 ? stringValue.substring(0, 500) + '...' : stringValue;
    })
  );

  const tableHeaders = columns.map(col => col.label || col.key);

  // Calculate start Y position for table (with spacing below header and details)
  const tableStartY = headerHeight; // Start right after details section with proper spacing
  
  // Calculate table width for centering (use 90% of page width, centered)
  const tableWidthPercent = 0.9; // 90% of page width
  const tableWidth = pageWidth * tableWidthPercent;
  const tableLeftMargin = (pageWidth - tableWidth) / 2; // Center the table
  
  const numColumns = tableHeaders.length;
  const baseColumnWidth = tableWidth / numColumns;
  
  // Build columnStyles with widths that allow text wrapping
  const columnStylesObj = {};
  tableHeaders.forEach((_, index) => {
    columnStylesObj[index] = {
      cellWidth: baseColumnWidth,
      overflow: 'linebreak',
    };
  });
  
  // Add table using autoTable (should be available on doc after side-effect import)
  if (doc.autoTable && typeof doc.autoTable === 'function') {
    doc.autoTable({
      head: [tableHeaders],
      body: tableData,
      startY: tableStartY,
      theme: 'grid', // Use grid theme for clear borders on all cells
      headStyles: {
        fillColor: companyBlue,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 7,
        cellPadding: 3,
        valign: 'middle',
        halign: 'left',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        overflow: 'linebreak',
        minCellHeight: 10,
      },
      bodyStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        overflow: 'linebreak',
        valign: 'top',
        cellPadding: 3,
      },
      styles: {
        fontSize: 7,
        cellPadding: 3,
        textColor: [0, 0, 0],
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        overflow: 'linebreak',
        valign: 'top',
        halign: 'left',
        minCellHeight: 6,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 0, left: tableLeftMargin, right: tableLeftMargin, bottom: 25 }, // Centered table
      showHead: 'everyPage', // Show header on every page
      showFoot: 'never', // No footer in table
      columnStyles: columnStylesObj,
      didDrawPage: function (data) {
        // Redraw header on each page (except first page where it's already drawn)
        if (data.pageNumber > 1) {
          drawHeader();
        }
        
        // Draw footer on each page
        drawFooter();
        
        // Add page numbers
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'normal');
        doc.text(
          `Page ${data.pageNumber} of ${data.pageCount}`,
          pageWidth / 2,
          pageHeight - 25,
          { align: 'center' }
        );
      },
    });
  } else {
    // Fallback: Create simple table without autoTable
    console.warn('autoTable not available, using simple table format');
    let yPos = 28;
    const rowHeight = 7;
    const colWidth = (doc.internal.pageSize.getWidth() - 28) / tableHeaders.length;
    
    // Draw headers
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    tableHeaders.forEach((header, i) => {
      doc.text(header, 14 + (i * colWidth), yPos);
    });
    yPos += rowHeight;
    
    // Draw rows
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    tableData.forEach((row) => {
      if (yPos > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage();
        yPos = 20;
      }
      row.forEach((cell, i) => {
        doc.text(String(cell || ''), 14 + (i * colWidth), yPos);
      });
      yPos += rowHeight;
    });
  }

  // Draw footer on first page
  drawFooter();
  
  // Save PDF
  doc.save(filename);
};

const exportUtils = {
  exportToCSV,
  exportToPDF,
};
export default exportUtils;

